import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { MdSend, MdAssignment, MdCheckCircle, MdPersonOff } from "react-icons/md";
import Card, { CardHeader } from "components/card";
import Avatar from "components/avatar";
import Badge from "components/badge";
import Button from "components/button";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState, ErrorState } from "components/states";
import { PageContainer, PageHeader } from "components/page";
import { useConfirm } from "components/dialog/ConfirmProvider";
import teamService from "services/teamService";
import { getApiErrorMessage } from "api";
import notify from "lib/notify";
import { getArchetype } from "lib/archetypes";
import { formatDate } from "utils/format";
import SubmissionCard from "./components/SubmissionCard";
import { fullName, roleLabel } from "./components/teamDisplay";

const BACK = { backTo: "/admin/team", backLabel: "Back to Org Chart" };

const ProfileSkeleton = () => (
  <PageContainer>
    <div className="flex items-center gap-3" aria-busy="true">
      <Skeleton className="h-10 w-10 rounded-control" />
      <Skeleton className="h-14 w-14 rounded-full" />
      <Skeleton lines={2} className="w-72" />
    </div>
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
      <Skeleton className="h-48 w-full rounded-card" />
      <Skeleton className="h-72 w-full rounded-card lg:col-span-2" />
    </div>
  </PageContainer>
);

/**
 * Full-page member view: who they are, their assessment, weekly check-in
 * history with notes, and leader actions (send assessment).
 */
const MemberProfile = () => {
  const { id } = useParams();
  const confirm = useConfirm();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [savingNote, setSavingNote] = useState(null);
  const [sendingAssessment, setSendingAssessment] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await teamService.getMemberProfile(id);
      setProfile(res.data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleSendAssessment = async () => {
    const confirmed = await confirm({
      title: `Send Assessment to ${fullName(profile?.user)}?`,
      message: `${profile?.user?.firstName} will get the assessment invite right away.`,
      confirmLabel: "Send",
    });
    if (!confirmed) return;
    try {
      setSendingAssessment(true);
      const res = await teamService.sendAssessment(id);
      const { status, reason, formName } = res.data || {};
      if (status === "sent") {
        notify.success(`Assessment "${formName}" sent`);
      } else {
        notify.info(reason || "Nothing sent");
      }
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not send assessment"));
    } finally {
      setSendingAssessment(false);
    }
  };

  const handleAddNote = async (submissionId) => {
    const text = (noteDrafts[submissionId] || "").trim();
    if (!text) return;
    try {
      setSavingNote(submissionId);
      const res = await teamService.addNote(submissionId, text);
      setProfile((p) => ({
        ...p,
        history: (p?.history || []).map((s) => (s.id === submissionId ? res.data : s)),
      }));
      setNoteDrafts((d) => ({ ...d, [submissionId]: "" }));
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not add note"));
    } finally {
      setSavingNote(null);
    }
  };

  if (loading) {
    return <ProfileSkeleton />;
  }

  if (error) {
    return (
      <PageContainer>
        <PageHeader title="Member Profile" {...BACK} />
        <ErrorState
          message={getApiErrorMessage(error, "We couldn't load this profile.")}
          onRetry={fetchProfile}
        />
      </PageContainer>
    );
  }

  if (!profile?.user) {
    return (
      <PageContainer>
        <PageHeader title="Member Profile" {...BACK} />
        <Card padding="md">
          <EmptyState
            icon={MdPersonOff}
            title="Profile Not Available"
            description="This person may have been removed from your organization."
            action={
              <Button as={Link} to="/admin/team" variant="secondary">
                Back to Org Chart
              </Button>
            }
          />
        </Card>
      </PageContainer>
    );
  }

  const { user, manager, directReportCount, assessment, history } = profile;
  const checkedInWeeks = (history || []).length;
  const archetype = getArchetype(assessment?.primaryStyle);
  const name = fullName(user);

  return (
    <PageContainer>
      <PageHeader
        {...BACK}
        title={name}
        leading={<Avatar user={user} name={name} size="lg" noBorder decorative />}
        meta={
          <>
            <Badge>{roleLabel(user.role)}</Badge>
            {directReportCount > 0 && (
              <Badge>
                {directReportCount} direct report{directReportCount === 1 ? "" : "s"}
              </Badge>
            )}
          </>
        }
        description={
          <>
            {user.jobTitle || roleLabel(user.role)}
            {manager ? (
              <>
                {" · reports to "}
                <span className="font-medium text-fg">{fullName(manager)}</span>
              </>
            ) : (
              " · top of the org chart"
            )}
            {user.email && <> · {user.email}</>}
          </>
        }
        actions={
          <Button
            leftIcon={<MdSend className="h-4 w-4" />}
            loading={sendingAssessment}
            onClick={handleSendAssessment}
          >
            Send Assessment
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Card padding="md" className="gap-3 lg:self-start">
          <div className="flex items-center gap-2">
            <MdAssignment aria-hidden="true" className="h-5 w-5 text-primary dark:text-accent" />
            <h2 className="text-heading">Assessment</h2>
          </div>
          {assessment?.primaryStyle ? (
            <div>
              <p className="text-2xl font-bold text-fg">
                {archetype?.label || assessment.primaryStyle}
              </p>
              {archetype?.description && (
                <p className="mt-1 text-sm text-fg-muted">{archetype.description}</p>
              )}
              {assessment.assessedAt && (
                <p className="mt-2 text-xs text-fg-muted">
                  Completed {formatDate(assessment.assessedAt)}
                </p>
              )}
              <Link
                to={`/admin/${user.role === "volunteer" ? "volunteers" : "staff"}/people/${user.id}`}
                className="focus-ring mt-3 inline-block rounded-control text-sm font-semibold text-primary hover:underline dark:text-accent"
              >
                View Full Assessment Details
              </Link>
            </div>
          ) : (
            <div>
              <p className="text-sm text-fg">No assessment completed yet.</p>
              <p className="mt-1 text-xs text-fg-muted">
                Use Send Assessment to invite them, or turn on automatic invites under Delivery on
                the Check-Ins page.
              </p>
            </div>
          )}
        </Card>

        <Card padding="md" className="gap-4 lg:col-span-2">
          <CardHeader
            title={
              <span className="flex items-center gap-2">
                <MdCheckCircle aria-hidden="true" className="h-5 w-5 text-primary dark:text-accent" />
                Check-In History
              </span>
            }
            description={
              checkedInWeeks
                ? `${checkedInWeeks} week${checkedInWeeks === 1 ? "" : "s"} submitted`
                : undefined
            }
          />

          {!checkedInWeeks ? (
            <EmptyState variant="inline" title="No check-ins submitted yet." />
          ) : (
            <div className="flex flex-col gap-3">
              {history.map((sub) => (
                <SubmissionCard
                  key={sub.id}
                  submission={sub}
                  columns={2}
                  draft={noteDrafts[sub.id]}
                  onDraftChange={(text) => setNoteDrafts((d) => ({ ...d, [sub.id]: text }))}
                  onAddNote={() => handleAddNote(sub.id)}
                  saving={savingNote === sub.id}
                />
              ))}
            </div>
          )}
        </Card>
      </div>
    </PageContainer>
  );
};

export default MemberProfile;
